import React, { useState, useMemo, useCallback, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Switch,
  Divider,
  CircularProgress,
  Alert,
  Skeleton,
  TextField,
  InputAdornment,
  Button,
} from '@mui/material';
import { BiSolidBus, BiPhone } from 'react-icons/bi';
import { PiCarFill, PiPackageFill, PiWrenchFill } from 'react-icons/pi';
import { toast } from 'react-toastify';
import { type GetUserDetailsQuery } from '../queries/getUserDetails.query.generated';
import { useSaveUserDetailsMutation } from '../queries/saveUserDetails.mutation.generated';
import { useDriverProducts } from '@features/drivers/hooks/useDriverProducts';
import { useRoleManagement } from '../hooks/useRoleManagement';
import { ATTRIBUTE_META, type MobilityAttribute } from '@utils/productMapper';
import { hasDriverRole } from '../utils/roleUtils';
import { formatPhone } from '@services/formatService';
import AppDownloadBadges from '@components/AppDownloadBadges';
import { useReloadGuard } from '@hopdrive/sdk-app-updater/react';

// -- Types --

type UserDetail = NonNullable<GetUserDetailsQuery['users']>[0];

interface UserDriverTabProps {
  user: UserDetail;
  refetch: () => void;
}

// -- Constants --

const ATTRIBUTE_ICONS: Record<MobilityAttribute, React.ComponentType<{ size: number }>> = {
  mobility_valet: PiCarFill,
  mobility_shuttle: BiSolidBus,
  mobility_service: PiWrenchFill,
  mobility_parts: PiPackageFill,
};

type Attributes = Partial<Record<MobilityAttribute, boolean>>;

// -- Component --

const UserDriverTab: React.FC<UserDriverTabProps> = ({ user, refetch }) => {
  const hasDriverRecord = !!user.driver;

  const { grantDriverAccess, revokeDriverAccess, setDriverAttribute } = useRoleManagement();
  const [updateUser] = useSaveUserDetailsMutation();
  const [togglingAccess, setTogglingAccess] = useState(false);
  const [savingAttr, setSavingAttr] = useState<MobilityAttribute | null>(null);

  // Phone prompt state for users missing a phone number
  const [showPhonePrompt, setShowPhonePrompt] = useState(false);
  const [phoneInput, setPhoneInput] = useState('');
  const [phoneFocused, setPhoneFocused] = useState(false);
  const [phoneError, setPhoneError] = useState<string>();

  // A typed phone number in the prompt, or a grant/revoke still running.
  useReloadGuard((showPhonePrompt && phoneInput !== '') || togglingAccess, 'Driver access');

  const userHasPhone = !!user.phone?.trim();

  // Check if user has the driver role in usertoroles
  const hasDriverAccess = hasDriverRole((user as any).users_usertoroles);

  // Always resolve products from customer configs (works for any user, not just drivers)
  const { enabledProducts, loading: productsLoading } = useDriverProducts(user.id);

  // Server attributes from driver.config (if driver record exists)
  const serverAttributes: Attributes = useMemo(() => {
    if (!user.driver) return {};
    const config = user.driver.config as Record<string, unknown> | null;
    return (config?.attributes as Attributes) ?? {};
  }, [user.driver]);

  // Local state for attributes when no driver record exists yet
  const [localAttributes, setLocalAttributes] = useState<Attributes>({});

  // Effective attributes: server if driver record exists, local otherwise
  const currentAttributes = hasDriverRecord ? serverAttributes : localAttributes;

  const visibleAttributes = useMemo(
    () =>
      [...enabledProducts].map(key => ({
        ...ATTRIBUTE_META[key],
        icon: ATTRIBUTE_ICONS[key],
      })),
    [enabledProducts]
  );

  // Auto-enable sole attribute when only one product is available
  useEffect(() => {
    if (productsLoading || enabledProducts.size !== 1) return;
    const onlyKey = [...enabledProducts][0];
    if (currentAttributes[onlyKey]) return;

    if (!hasDriverRecord) {
      setLocalAttributes(prev => ({ ...prev, [onlyKey]: true }));
    } else if (user.driver) {
      setDriverAttribute(user.driver.id, user.id, currentAttributes, onlyKey, true)
        .then(() => refetch())
        .catch(() => {}); // fail silently — admin can toggle manually
    }
  }, [productsLoading, enabledProducts]);

  const handleToggleAttribute = useCallback(
    async (key: MobilityAttribute, checked: boolean) => {
      if (!hasDriverRecord) {
        // No driver record — just update local state
        setLocalAttributes(prev => ({ ...prev, [key]: checked }));
        return;
      }

      // Driver record exists — persist directly
      setSavingAttr(key);
      try {
        await setDriverAttribute(user.driver!.id, user.id, currentAttributes, key, checked);
        toast.success(`${checked ? 'Enabled' : 'Disabled'} ${ATTRIBUTE_META[key].label}`);
        refetch();
      } catch (err: any) {
        toast.error(`Failed to update attribute: ${err.message}`);
      } finally {
        setSavingAttr(null);
      }
    },
    [hasDriverRecord, currentAttributes, user.driver, user.id, setDriverAttribute, refetch]
  );

  const handleToggleDriverAccess = useCallback(
    async (grant: boolean) => {
      if (grant && !userHasPhone) {
        setShowPhonePrompt(true);
        return;
      }

      setTogglingAccess(true);
      try {
        if (grant) {
          await grantDriverAccess(user.id, hasDriverRecord, currentAttributes);
          toast.success('Mobile app access granted');
        } else {
          await revokeDriverAccess(user.id);
          toast.success('Mobile app access removed');
        }
        refetch();
      } catch (err: any) {
        toast.error(`Failed to update driver access: ${err.message}`);
      } finally {
        setTogglingAccess(false);
      }
    },
    [user.id, hasDriverRecord, currentAttributes, userHasPhone, grantDriverAccess, revokeDriverAccess, refetch]
  );

  const handlePhoneSubmitAndGrant = useCallback(async () => {
    const digits = phoneInput.replace(/\D/g, '');
    if (digits.length < 10) {
      setPhoneError('A valid 10-digit phone number is required');
      return;
    }

    setTogglingAccess(true);
    setPhoneError(undefined);
    try {
      await updateUser({
        variables: { userId: user.id, userObj: { phone: digits } },
      });
      await grantDriverAccess(user.id, hasDriverRecord, currentAttributes);
      toast.success('Phone saved and mobile app access granted');
      setShowPhonePrompt(false);
      setPhoneInput('');
      refetch();
    } catch (err: any) {
      toast.error(`Failed to grant driver access: ${err.message}`);
    } finally {
      setTogglingAccess(false);
    }
  }, [phoneInput, user.id, hasDriverRecord, currentAttributes, updateUser, grantDriverAccess, refetch]);

  return (
    <Box>
      {/* Mobile App Access Toggle */}
      <Paper sx={{ p: 3, mb: 2 }}>
        <Typography variant='h6' sx={{ mb: 1 }}>
          Mobile App Access
        </Typography>
        <Divider sx={{ mb: 2 }} />

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            py: 1,
          }}
        >
          <Box>
            <Typography variant='body2' fontWeight={500}>
              HopDrive Mobile App
            </Typography>
            <Typography variant='caption' color='text.secondary'>
              {hasDriverAccess
                ? 'This user can access the HopDrive mobile app to perform moves.'
                : 'Grant this user access to the HopDrive mobile app.'}
            </Typography>
          </Box>
          <Switch
            checked={hasDriverAccess}
            onChange={(_, checked) => handleToggleDriverAccess(checked)}
            disabled={togglingAccess || showPhonePrompt}
          />
        </Box>

        {showPhonePrompt && (
          <Box sx={{ mt: 2, p: 2, borderRadius: 1, backgroundColor: 'action.hover' }}>
            <Typography variant='body2' sx={{ fontWeight: 500, mb: 0.5 }}>
              Phone number required
            </Typography>
            <Typography variant='caption' color='text.secondary' sx={{ display: 'block', mb: 1.5 }}>
              This must be the driver's personal cell phone. Our platform uses this number to book rideshare trips
              (Uber/Lyft) on the driver's behalf — without a valid mobile number, rides cannot be ordered for them.
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
              <TextField
                placeholder='e.g. (555) 123-4567'
                size='small'
                value={!phoneFocused && phoneInput ? (formatPhone(phoneInput) ?? phoneInput) : phoneInput}
                onChange={e => {
                  const digits = e.target.value.replace(/\D/g, '').slice(0, 10);
                  setPhoneInput(digits);
                  if (phoneError) setPhoneError(undefined);
                }}
                onFocus={() => setPhoneFocused(true)}
                onBlur={() => setPhoneFocused(false)}
                error={!!phoneError}
                helperText={phoneError}
                sx={{ flex: 1 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position='start'>
                      <BiPhone size={18} />
                    </InputAdornment>
                  ),
                }}
              />
              <Button
                variant='contained'
                size='small'
                onClick={handlePhoneSubmitAndGrant}
                disabled={togglingAccess}
                sx={{ textTransform: 'none', whiteSpace: 'nowrap', mt: '1px' }}
              >
                {togglingAccess ? <CircularProgress size={16} /> : 'Save & Enable'}
              </Button>
              <Button
                variant='text'
                size='small'
                onClick={() => {
                  setShowPhonePrompt(false);
                  setPhoneInput('');
                  setPhoneError(undefined);
                }}
                disabled={togglingAccess}
                sx={{ textTransform: 'none', mt: '1px' }}
              >
                Cancel
              </Button>
            </Box>
          </Box>
        )}

        <Divider sx={{ my: 1.5 }} />
        <AppDownloadBadges compact label='Get the HopDrive Driver app' />
      </Paper>

      {/* Driver Attributes — always shown so admin can configure before enabling */}
      <Paper sx={{ p: 3, mb: 2 }}>
        <Typography variant='h6' sx={{ mb: 1 }}>
          Driver Attributes
        </Typography>
        <Typography variant='body2' color='text.secondary' sx={{ mb: 1 }}>
          {hasDriverAccess
            ? 'Control what types of moves this driver can be assigned to.'
            : 'Configure attributes before enabling mobile app access.'}
        </Typography>
        <Divider sx={{ mb: 2 }} />

        {productsLoading && (
          <Box>
            {[1, 2].map(i => (
              <Box key={i} display='flex' alignItems='center' gap={1.5} py={1.5} px={1}>
                <Skeleton variant='rounded' width={36} height={36} />
                <Box flex={1}>
                  <Skeleton width={80} height={20} />
                  <Skeleton width={200} height={16} />
                </Box>
                <Skeleton variant='rounded' width={40} height={20} />
              </Box>
            ))}
          </Box>
        )}

        {!productsLoading && visibleAttributes.length === 0 && (
          <Alert severity='info'>No products are enabled across this user's linked markets.</Alert>
        )}

        {!productsLoading &&
          visibleAttributes.map(attr => {
            const IconComponent = attr.icon;
            const isOn = !!currentAttributes[attr.key];
            const isSaving = savingAttr === attr.key;

            return (
              <Box
                key={attr.key}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  py: 1.5,
                  px: 1,
                  borderRadius: 1,
                  '&:hover': { backgroundColor: 'rgba(0,0,0,0.02)' },
                }}
              >
                <Box display='flex' alignItems='center' gap={1.5}>
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: isOn ? '#eff6ff' : '#f4f4f5',
                      color: isOn ? '#2563eb' : '#9ca3af',
                      transition: 'all 150ms',
                    }}
                  >
                    <IconComponent size={20} />
                  </Box>
                  <Box>
                    <Typography sx={{ fontSize: 14, fontWeight: 500 }}>{attr.label}</Typography>
                    <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>{attr.description}</Typography>
                  </Box>
                </Box>

                <Box display='flex' alignItems='center' gap={1}>
                  {isSaving && <CircularProgress size={16} />}
                  <Switch
                    checked={isOn}
                    disabled={isSaving || visibleAttributes.length === 1}
                    onChange={(_, checked) => handleToggleAttribute(attr.key, checked)}
                    size='small'
                  />
                </Box>
              </Box>
            );
          })}
      </Paper>
    </Box>
  );
};

export default UserDriverTab;
